/**
 * Purge-target registry — "which pages can the admin panel invalidate, and how".
 *
 * Unlike the stock registries in data-stock.ts (hand-transcribed from C#), the
 * page lists here are **derived from @tipranks/links**, the repo's own copy of
 * the legacy `trLinks.json` route registry. That is deliberate: the legacy purge
 * (`tipranks-apps/packages/app-assets/src/server/assets.ts` → `getRoutes`) builds
 * its URLs from exactly these link ids, so deriving them removes the silent
 * wrong-URL drift risk that hand transcription carries.
 *
 * Three purge mechanisms exist because the site genuinely has three:
 *
 *  1. `url`            — the page is rendered on demand; soft-purging its HTML
 *                        is the whole job. Experts, calendars, screeners,
 *                        marketing/static pages.
 *  2. `stock-bff`      — a stock tab: purge the Worker's `asset:stock:<t>` tag
 *                        (+ warm) and soft-purge the tab's HTML on Fastly. The
 *                        Worker reads website-api (Mongo), so no payload
 *                        refresh is needed. The key keeps its old name.
 *  3. `assets-rebuild` — an asset (ETF, fund, coin, index, currency, commodity,
 *                        private company): purge the Worker's `asset:<class>:<t>`
 *                        tag (+ warm) and soft-purge every tab on Fastly. The
 *                        Worker reads website-api, so no payload rebuild is
 *                        needed; the legacy rebuild host was deleted 2026-11-04.
 *                        The key keeps its old name.
 */

import { type LinkKey, links, meta } from "@tipranks/links";
import { STOCK_PAGE_TYPES, type StockPageTypeDef } from "./data-stock.js";

// ── mechanisms ──

/**
 * Asset type keys the upstream assets function accepts as `?asset=` — these are
 * the legacy internal names (`fund`, `coin`, `forex`), NOT the URL segments
 * (`mutual-funds`, `cryptocurrency`, `currencies`). Mirrors the keys of
 * `mapSideMenu` / `configAsset` in tipranks-apps.
 */
export const ASSET_TYPE_KEYS = [
  "etf",
  "fund",
  "coin",
  "index",
  "forex",
  "commodity",
  "private-company",
] as const;

export type AssetTypeKey = (typeof ASSET_TYPE_KEYS)[number];

export type PurgeMechanism =
  | { kind: "url" }
  | { kind: "stock-bff" }
  | { kind: "assets-rebuild"; assetType: AssetTypeKey };

export type PurgeMechanismKind = PurgeMechanism["kind"];

/**
 * Which edge serves a family's pages. The two use different purge APIs, so
 * getting this wrong purges a URL that was never cached and reports success:
 *
 * - `website` — www.tipranks.com, Fastly soft purge, per-URL results.
 * - `il`      — the Hebrew site (getIlWebsiteBase), Cloudflare URL purge. Cloudflare
 *               reports one outcome per batch, not per URL.
 */
export type PurgeHost = "website" | "il";

/**
 * Path prefixes served by the Hebrew site rather than www. Derived from the
 * Hebrew pages' own routes; `purgeIlArticle` in the admin API already treats
 * this host as Cloudflare-fronted.
 */
const IL_PATH_PREFIXES: readonly string[] = ["/feed", "/news-heb"];

export function purgeHostForPath(path: string): PurgeHost {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return IL_PATH_PREFIXES.some(
    (prefix) => normalized === prefix || normalized.startsWith(`${prefix}/`)
  )
    ? "il"
    : "website";
}

// ── params ──

export interface PurgeParamSpec {
  /** Template slot name, e.g. `ticker` / `slug` / `marketCountry`. */
  name: string;
  label: string;
  /** When set, the value must be one of these (the UI renders a select). */
  allowed?: readonly string[];
  placeholder?: string;
}

/** Human labels + placeholders for the slots that appear across the registry. */
const PARAM_LABELS: Record<string, { label: string; placeholder?: string }> = {
  ticker: { label: "Ticker", placeholder: "AAPL" },
  slug: { label: "Slug", placeholder: "john-smith" },
  marketCountry: { label: "Market" },
  date: { label: "Date", placeholder: "2026-07-26" },
  type: { label: "Type" },
  category: { label: "Category", placeholder: "analysis" },
  topic: { label: "Topic", placeholder: "aapl" },
  portfolio: { label: "Portfolio", placeholder: "123456" },
  letter: { label: "Letter", placeholder: "a" },
  page: { label: "Page", placeholder: "1" },
  period: { label: "Period", placeholder: "2026-q1" },
  report: { label: "Report", placeholder: "income-statement" },
  dimension: { label: "Dimension", placeholder: "by-aum" },
  investorSlug: { label: "Investor", placeholder: "sequoia-capital" },
  sectorSlug: { label: "Sector", placeholder: "fintech" },
  plan: { label: "Plan" },
  tab: { label: "Tab" },
};

/**
 * Enum overrides for slots the registry declares as free-form but which really
 * are closed sets. Keep this tiny — the registry's own `params` arrays are the
 * primary source, and every entry here is a place the registry is under-typed.
 */
const PARAM_ALLOWED_OVERRIDES: Partial<Record<LinkKey, Record<string, readonly string[]>>> = {
  // `/experts/:type/:slug` — the five expert directories the site serves.
  expert: {
    type: ["analysts", "bloggers", "insiders", "hedge-funds", "investors"],
  },
};

// ── path resolution ──

const PARAM_RE = /:([A-Za-z0-9_]+)(\??)/g;

/**
 * Param slots a link template needs, in order. `optional` mirrors the `?`
 * marker a few templates carry (e.g. `/news/:category?/:slug`).
 */
export function templateParams(
  key: LinkKey
): readonly { name: string; optional: boolean }[] {
  const link = links[key]?.link;
  if (!link) return [];
  const out: { name: string; optional: boolean }[] = [];
  for (const m of link.matchAll(PARAM_RE)) {
    out.push({ name: m[1], optional: m[2] === "?" });
  }
  return out;
}

/** Allowed values for one slot of one page, or undefined when free-form. */
export function paramAllowed(
  key: LinkKey,
  name: string
): readonly string[] | undefined {
  const override = PARAM_ALLOWED_OVERRIDES[key]?.[name];
  if (override) return override;
  const declared = meta(key).params?.[name];
  return Array.isArray(declared) ? declared : undefined;
}

export class PurgePathError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PurgePathError";
  }
}

/**
 * Resolve a link template to a site-absolute path for purging.
 *
 * This deliberately does NOT use `@tipranks/links`' `href()`, which cannot serve
 * this use case: it percent-encodes values (mangling the `:` in exchange-prefixed
 * tickers like `TSE:XYZ`), throws on the `?`-marked optional slots, and returns
 * a handful of templates that are missing their leading slash (`experts/firms/:slug`,
 * `sign-up`, `mutual-funds`). All three would produce a silently wrong purge URL,
 * so path building for purging lives here and is covered by data-purge.test.ts.
 *
 * Values are lowercased, matching every URL builder on both stacks.
 */
export function resolvePurgePath(
  key: LinkKey,
  params: Readonly<Record<string, string>> = {}
): string {
  const template = links[key]?.link;
  if (!template) {
    throw new PurgePathError(`Link "${key}" has no URL template`);
  }
  if (/^[a-z]+:\/\/|^\/\//i.test(template)) {
    throw new PurgePathError(`Link "${key}" is external: ${template}`);
  }

  let path = template.replace(PARAM_RE, (_m, name: string, optional: string) => {
    const raw = params[name]?.trim();
    if (!raw) {
      if (optional === "?") return "";
      throw new PurgePathError(`Missing param "${name}" for page "${key}"`);
    }
    const value = normalizeParamValue(name, raw);
    const allowed = paramAllowed(key, name);
    if (allowed && !allowed.includes(value)) {
      throw new PurgePathError(
        `Invalid ${name} "${value}" for page "${key}". Allowed: ${allowed.join(", ")}`
      );
    }
    return value;
  });

  // Normalize the registry's inconsistencies: a missing leading slash, the
  // empty segments an omitted optional slot leaves behind, and a trailing
  // slash (`/calendars/economic/`) that would purge a different cache object.
  path = `/${path}`.replace(/\/{2,}/g, "/");
  if (path.length > 1) path = path.replace(/\/+$/, "");
  return path;
}

/**
 * Ticker/slug normalization shared with the legacy purge: lowercase, and strip
 * the `fx:` prefix that forex tickers carry internally but never in a URL
 * (`app-assets/src/server/assets.ts:51`). Exchange prefixes on stock tickers
 * (`TSE:XYZ`) stay as-is — they are part of the path.
 */
function normalizeParamValue(name: string, raw: string): string {
  const value = raw.toLowerCase();
  return name === "ticker" ? value.replace(/^fx:/, "") : value;
}

/** True when every required slot of the page has a usable value. */
export function canResolvePage(
  key: LinkKey,
  params: Readonly<Record<string, string>> = {}
): boolean {
  try {
    resolvePurgePath(key, params);
    return true;
  } catch {
    return false;
  }
}

// ── families ──

export interface PurgeFamilyDef {
  key: string;
  label: string;
  /** Heading the UI groups this family under. */
  group: string;
  description: string;
  /** Registry keys of the pages in this family, in site menu order. */
  pageKeys: readonly LinkKey[];
  mechanism: PurgeMechanism;
  /** Which edge serves these pages — picks the purge API. */
  host: PurgeHost;
  /**
   * True when the mechanism acts on the whole entity rather than a page: the
   * asset's Worker tag covers every tab, so per-page
   * selection would be a lie. The UI renders the page list read-only.
   */
  wholeEntityOnly: boolean;
  /**
   * Also purge `{path}/payload.json` for each page. The legacy loader appends
   * `/payload.json` to the route path verbatim (`tr-apis/src/api/config.ts:59`),
   * so that sibling is the page's server-state object. Set per family rather
   * than per page — purging a URL with no cached object is a no-op, so
   * over-covering here is safe and under-covering is not.
   */
  hasPayloadSibling: boolean;
}

/**
 * Asset tab lists, transcribed once from the legacy side-menu configs
 * (`app-assets/src/data/organism/SideMenu*.json`) — the exact set
 * `purgeAsset()` purges. Every id is asserted to resolve in data-purge.test.ts.
 *
 * The registry also carries asset pages these menus omit (`etf-chart-advanced`,
 * `etf-options-activity`, `crypto-advanced-chart`, the `/{family}` centre
 * pages). Those are intentionally left to the auto-derived tier below, where
 * they get a plain URL purge — the upstream trigger does not cover them.
 */
const ASSET_FAMILIES: readonly PurgeFamilyDef[] = [
  {
    key: "etf",
    label: "ETF",
    group: "Assets",
    description: "Purge an ETF's Worker cache tag and all its tabs",
    mechanism: { kind: "assets-rebuild", assetType: "etf" },
    host: "website",
    wholeEntityOnly: true,
    hasPayloadSibling: false,
    pageKeys: [
      "etf-analysis",
      "etf-forecast",
      "etf-holdings",
      "etf-ai-analyst",
      "etf-news",
      "etf-dividends",
      "etf-options",
      "etf-technical-analysis",
      "etf-charts",
      "etf-investors",
      "etf-hedge-funds",
      "etf-similar",
      "etf-heatmap",
    ],
  },
  {
    key: "mutual-fund",
    label: "Mutual Fund",
    group: "Assets",
    description: "Purge a fund's Worker cache tag and all its tabs",
    mechanism: { kind: "assets-rebuild", assetType: "fund" },
    host: "website",
    wholeEntityOnly: true,
    hasPayloadSibling: false,
    pageKeys: [
      "mutual-funds-analysis",
      "mutual-funds-forecast",
      "mutual-funds-holdings",
      "mutual-funds-dividends",
      "mutual-funds-technical-analysis",
      "mutual-funds-performance",
      "mutual-funds-similar",
    ],
  },
  {
    key: "crypto",
    label: "Cryptocurrency",
    group: "Assets",
    description: "Purge a coin's Worker cache tag and all its tabs",
    mechanism: { kind: "assets-rebuild", assetType: "coin" },
    host: "website",
    wholeEntityOnly: true,
    hasPayloadSibling: false,
    pageKeys: [
      "crypto-overview",
      "crypto-technical-analysis",
      "crypto-chart",
      "crypto-performance",
      "crypto-news",
      "crypto-historical",
      "crypto-similar",
    ],
  },
  {
    key: "index",
    label: "Index",
    group: "Assets",
    description: "Purge an index's Worker cache tag and all its tabs",
    mechanism: { kind: "assets-rebuild", assetType: "index" },
    host: "website",
    wholeEntityOnly: true,
    hasPayloadSibling: false,
    pageKeys: [
      "index-analysis",
      "index-holdings",
      "index-etfs",
      "index-earnings",
      "index-heatmap",
    ],
  },
  {
    key: "currency",
    label: "Currency",
    group: "Assets",
    description: "Purge a currency pair's Worker cache tag and all its tabs",
    mechanism: { kind: "assets-rebuild", assetType: "forex" },
    host: "website",
    wholeEntityOnly: true,
    hasPayloadSibling: false,
    pageKeys: [
      "currencies-analysis",
      "currencies-economic",
      "currencies-technical-analysis",
      "currencies-etfs",
      "currencies-news",
    ],
  },
  {
    key: "commodity",
    label: "Commodity",
    group: "Assets",
    description: "Purge a commodity's Worker cache tag and all its tabs",
    mechanism: { kind: "assets-rebuild", assetType: "commodity" },
    host: "website",
    wholeEntityOnly: true,
    hasPayloadSibling: false,
    pageKeys: [
      "commodities-analysis",
      "commodities-technical-analysis",
      "commodities-etfs",
      "commodities-news",
    ],
  },
  {
    key: "private-company",
    label: "Private Company",
    group: "Assets",
    description: "Purge a private company's Worker cache tag and its page",
    mechanism: { kind: "assets-rebuild", assetType: "private-company" },
    host: "website",
    wholeEntityOnly: true,
    hasPayloadSibling: false,
    pageKeys: ["private-company"],
  },
];

// ── stock family ──

const STOCK_PATH_PREFIX = "/stocks/:ticker";

/**
 * Registry path (relative to `/stocks/{ticker}`) → the canonical link key.
 *
 * Only entries whose template carries the leading slash are accepted, which
 * drops the registry's malformed duplicate (`stocks/:ticker/drug-pipeline`);
 * templates with a leftover slot (`/:tab`, `/financials/:report`) are catch-alls
 * that the concrete entries already cover.
 */
const STOCK_PATH_TO_LINK_KEY: ReadonlyMap<string, LinkKey> = (() => {
  const map = new Map<string, LinkKey>();
  for (const [key, entry] of Object.entries(links)) {
    const link = entry.link;
    if (!link?.startsWith(STOCK_PATH_PREFIX)) continue;
    const path = link.slice(STOCK_PATH_PREFIX.length);
    if (path.includes(":")) continue;
    if (!map.has(path)) map.set(path, key as LinkKey);
  }
  return map;
})();

/**
 * The stock tab family. Its pages are the C#-derived STOCK_PAGE_TYPES, mapped
 * onto their registry keys so stocks share one shape (and one UI code path)
 * with every other family. The `financials` type owns five registry keys — one
 * page type, five HTML URLs — so selecting any of them purges all five once;
 * see `stockPageTypesForLinkKeys`.
 */
export const STOCK_PURGE_FAMILY: PurgeFamilyDef = {
  key: "stock",
  label: "Stock",
  group: "Assets",
  description:
    "Purge a stock tab: the Worker cache tag (+ warm) and its HTML on Fastly",
  mechanism: { kind: "stock-bff" },
  host: "website",
  wholeEntityOnly: false,
  hasPayloadSibling: false,
  pageKeys: STOCK_PAGE_TYPES.flatMap((page) =>
    page.websitePaths
      .map((path) => STOCK_PATH_TO_LINK_KEY.get(path))
      .filter((key): key is LinkKey => key !== undefined)
  ),
};

/**
 * Stock tabs the C# CdnOrchestrator has no page type for, so they carry no BFF
 * payload we can refresh — a plain HTML purge is all that is available.
 */
const STOCK_EXTRA_FAMILY: PurgeFamilyDef = {
  key: "stock-extra",
  label: "Stock (no payload)",
  group: "Assets",
  description:
    "Stock tabs with no BFF payload on the data platform — HTML purge only",
  mechanism: { kind: "url" },
  host: "website",
  wholeEntityOnly: false,
  hasPayloadSibling: false,
  pageKeys: [
    "stock-community",
    "stock-marketcap",
    "stock-revenue",
    "stock-options-activity",
    // KPIs (TR-26138). The BFF *does* serve
    // `/stocks/{t}/stock-kpis/payload.json` — but `TipRanksBffUrlBuilder.cs`
    // has no Build…PayloadUrl for it (checked 2026-09-06: 23 builders, none
    // KPIs), so the CdnOrchestrator cannot refresh that payload and a
    // STOCK_PAGE_TYPES entry would offer a purge the data-plane can't perform.
    // Move it there once the C# side adds a builder.
    "stock-kpis",
  ],
};

/**
 * Map selected stock link keys back to the page types whose payload must be
 * refreshed, deduped — five Financials URLs share one payload.
 */
export function stockPageTypesForLinkKeys(
  pageKeys: readonly string[]
): readonly StockPageTypeDef[] {
  return STOCK_PAGE_TYPES.filter((page) =>
    page.websitePaths.some((path) => {
      const key = STOCK_PATH_TO_LINK_KEY.get(path);
      return key !== undefined && pageKeys.includes(key);
    })
  );
}

/**
 * Families with a curated page list because their mechanism or grouping is
 * special. Everything else is auto-derived below.
 */
const CURATED_FAMILIES: readonly PurgeFamilyDef[] = [
  STOCK_PURGE_FAMILY,
  STOCK_EXTRA_FAMILY,
  ...ASSET_FAMILIES,
  {
    key: "expert",
    label: "Expert",
    group: "Experts",
    description:
      "Analyst / blogger / insider / hedge-fund / investor profile pages. No payload — rendered from live APIs, so HTML purge is the whole job.",
    mechanism: { kind: "url" },
    host: "website",
    wholeEntityOnly: false,
    hasPayloadSibling: false,
    pageKeys: ["expert"],
  },
  {
    key: "firm",
    label: "Research Firm",
    group: "Experts",
    description: "A research firm's profile page",
    mechanism: { kind: "url" },
    host: "website",
    wholeEntityOnly: false,
    hasPayloadSibling: false,
    pageKeys: ["firm"],
  },
  {
    key: "expert-directories",
    label: "Expert Directories",
    group: "Experts",
    description: "The expert centre and the per-type top-expert leaderboards",
    mechanism: { kind: "url" },
    host: "website",
    wholeEntityOnly: false,
    hasPayloadSibling: false,
    pageKeys: [
      "top-experts",
      "experts-top",
      "experts-analysts",
      "experts-bloggers",
      "experts-insiders",
      "experts-hedge-funds",
      "experts-investors",
      "firms-top",
      "how-experts-ranked",
    ],
  },
  {
    key: "public-portfolio",
    label: "Public Portfolio",
    group: "Experts",
    description: "A publicly shared portfolio under /experts/investors",
    mechanism: { kind: "url" },
    host: "website",
    wholeEntityOnly: false,
    hasPayloadSibling: false,
    pageKeys: ["portfolio", "public-portfolio-holding"],
  },
];

/**
 * Link keys the auto-derived tier must never surface.
 *
 * `/{family}/:ticker/:tab` catch-alls duplicate the concrete tab entries with a
 * free-form slot, and `smart-investor-article` is a bare `:url` passthrough —
 * both would only let an operator build a URL by hand, which is what this
 * registry exists to avoid.
 */
const EXCLUDED_KEYS: readonly string[] = [
  "etf",
  "crypto",
  "index",
  "mutual-funds",
  "currencies",
  "commodities",
  "stock",
  "smart-investor-article",
];

/**
 * Path prefixes that are per-user and never cached at the edge, so purging them
 * is meaningless. Kept as a named list rather than an inline regex because the
 * public `/experts/investors/...` portfolio pages live in the same legacy app
 * and must NOT be caught by it.
 */
const PRIVATE_PATH_PREFIXES: readonly string[] = [
  "/account",
  "/smart-portfolio",
  "/checkout",
  "/sign-in",
  "/sign-up",
  "/change-email",
  "/verify",
];

/** Registry `app` → the heading the UI groups its pages under. */
const APP_GROUP_LABELS: Record<string, string> = {
  "app-stocks": "Stock Pages",
  "app-assets": "Asset Pages",
  "app-research": "Screeners & Research",
  "app-calendars": "Calendars",
  "app-marketing": "Marketing & Landing",
  "app-blog": "News & Blog",
  "app-blogtrans": "News (Translated)",
  "app-general": "Static & Legal",
  "app-home": "Homepage",
  "app-experts": "Experts",
  "app-portfolio": "Portfolio",
  "app-markets": "Markets",
  "app-assistant": "Assistant",
  "app-tipranks": "TipRanks Pages",
  "app-tipranks-hack": "TipRanks Pages",
  "tr-marketing": "Marketing & Landing",
  external: "Other",
};

function isPurgeableEntry(key: string): boolean {
  const entry = links[key as LinkKey];
  const link = entry?.link;
  if (!link) return false;
  if (/^[a-z]+:\/\/|^\/\//i.test(link)) return false;
  if (EXCLUDED_KEYS.includes(key)) return false;
  if (/\/:tab\??$/.test(link)) return false;
  const path = link.startsWith("/") ? link : `/${link}`;
  // Every stock tab is claimed by STOCK_PURGE_FAMILY or STOCK_EXTRA_FAMILY;
  // anything left here is a catch-all or a malformed duplicate.
  if (path.startsWith(`${STOCK_PATH_PREFIX}/`)) return false;
  return !PRIVATE_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
}

/**
 * Every purgeable registry page not claimed by a curated family, grouped by the
 * registry's own `app` field. This is what makes the purge cover the whole site
 * without transcribing 340 routes: adding a page to links.json makes it
 * purgeable here for free.
 */
function deriveRemainingFamilies(): readonly PurgeFamilyDef[] {
  const claimed = new Set<string>(
    CURATED_FAMILIES.flatMap((f) => f.pageKeys as readonly string[])
  );
  const buckets = new Map<
    string,
    { label: string; host: PurgeHost; keys: LinkKey[] }
  >();

  for (const key of Object.keys(links)) {
    if (claimed.has(key) || !isPurgeableEntry(key)) continue;
    const entry = links[key as LinkKey];
    const app = entry.app ?? "external";
    // Split by host as well as app: the Hebrew pages sit in app-blogtrans
    // alongside the English ones but are served from a different edge.
    const host = purgeHostForPath(entry.link!);
    const label =
      host === "il"
        ? "News (Hebrew site)"
        : (APP_GROUP_LABELS[app] ?? "Other");
    const bucket = buckets.get(label) ?? { label, host, keys: [] };
    bucket.keys.push(key as LinkKey);
    buckets.set(label, bucket);
  }

  return [...buckets.values()]
    .map(({ label, host, keys }) => ({
      // Family keys travel over HTTP, so keep them clean: labels contain
      // punctuation ("News (Hebrew site)") that would leave stray dashes.
      key: `pages:${label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")}`,
      label,
      group: "Other Pages",
      description:
        host === "il"
          ? "Purge Hebrew pages from the Hebrew site (Cloudflare)"
          : `Soft-purge the HTML of ${label.toLowerCase()} pages`,
      pageKeys: keys,
      mechanism: { kind: "url" } as const,
      host,
      wholeEntityOnly: false,
      // The dynamic-payload families (calendars, screeners) serve their server
      // state at `{path}/payload.json`; the static ones simply have no such
      // object, and purging it is a no-op.
      hasPayloadSibling: true,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export const PURGE_FAMILIES: readonly PurgeFamilyDef[] = [
  ...CURATED_FAMILIES,
  ...deriveRemainingFamilies(),
];

export function getPurgeFamily(key: string): PurgeFamilyDef | undefined {
  return PURGE_FAMILIES.find((f) => f.key === key);
}

/**
 * The param slots the family's pages need, deduped, with labels and any
 * enumerated values. `allowed` unions the per-page sets (pages enumerate
 * different market lists); per-page validation still happens in
 * `resolvePurgePath`, and the UI should gate on `canResolvePage`.
 */
export function getFamilyParams(
  family: PurgeFamilyDef
): readonly PurgeParamSpec[] {
  const specs = new Map<string, { allowed: Set<string> | null }>();

  for (const pageKey of family.pageKeys) {
    for (const { name } of templateParams(pageKey)) {
      const allowed = paramAllowed(pageKey, name);
      const existing = specs.get(name);
      if (!existing) {
        specs.set(name, { allowed: allowed ? new Set(allowed) : null });
        continue;
      }
      // Any page treating the slot as free-form makes the union free-form.
      if (!allowed) existing.allowed = null;
      else if (existing.allowed) allowed.forEach((v) => existing.allowed!.add(v));
    }
  }

  return [...specs.entries()].map(([name, { allowed }]) => ({
    name,
    label: PARAM_LABELS[name]?.label ?? name,
    placeholder: PARAM_LABELS[name]?.placeholder,
    allowed: allowed ? [...allowed] : undefined,
  }));
}

/**
 * Human label for a page in the purge UI.
 *
 * The registry's `text` is nav copy, so it is sometimes a raw identifier
 * (`aianalyst`, `drugPipeline`) and sometimes ambiguous — all five Financials
 * URLs are just "Financials". Stock tabs therefore use their C#-derived
 * STOCK_PAGE_TYPES label plus the distinguishing sub-path; everything else
 * falls back to humanized registry text, with the resolved path shown beneath
 * it in the UI.
 */
export function purgePageLabel(key: LinkKey): string {
  const stockLabel = STOCK_PAGE_LABELS.get(key);
  if (stockLabel) return stockLabel;
  const text = meta(key).text;
  if (text) return humanizeLabel(text);
  const link = links[key]?.link ?? key;
  const tail = link.split("/").filter((s) => s && !s.startsWith(":")).pop();
  return humanizeLabel(tail ?? key);
}

/** `drugPipeline` / `income-statement` → `Drug Pipeline` / `Income Statement`. */
function humanizeLabel(raw: string): string {
  const spaced = raw
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[-_]+/g, " ")
    .trim();
  return spaced.replace(/\b[a-z]/g, (c) => c.toUpperCase());
}

/**
 * Stock link key → label, built from STOCK_PAGE_TYPES so the tab names match
 * what the rest of the Data section calls them. Page types with a fan-out get
 * the sub-path appended (`Financials — Income Statement`).
 */
const STOCK_PAGE_LABELS: ReadonlyMap<LinkKey, string> = (() => {
  const map = new Map<LinkKey, string>();
  for (const page of STOCK_PAGE_TYPES) {
    for (const path of page.websitePaths) {
      const key = STOCK_PATH_TO_LINK_KEY.get(path);
      if (!key) continue;
      const suffix =
        page.websitePaths.length > 1 && path.includes("/", 1)
          ? ` — ${humanizeLabel(path.split("/").pop()!)}`
          : "";
      map.set(key, `${page.label}${suffix}`);
    }
  }
  return map;
})();

// ── API shapes ──

export interface PurgeRequest {
  family: string;
  /** Registry keys, or "all" for every page in the family. */
  pageKeys: string[] | "all";
  /** Template slot values (ticker / slug / marketCountry / …). */
  params?: Record<string, string>;
}

/**
 * Which CDN a purge reached:
 * - `cloudflare-tags` — the Worker's Cache API entries (www pages the Worker answers),
 *                       then `/_tr/warm` for its KV copy;
 * - `fastly-urls`     — Fastly soft purge (legacy-served www pages; kept as a dual
 *                       purge so a rollback to legacy never resurfaces stale copies);
 * - `cloudflare-urls` — Cloudflare URL purge (the Hebrew site).
 */
export type PurgeCdn = "cloudflare-tags" | "fastly-urls" | "cloudflare-urls";

export interface PurgeUrlResult {
  url: string;
  ok: boolean;
  status: number;
  purgeId: string | null;
  /** CDN(s) this URL's purge reached. Absent on the single-CDN stock paths. */
  cdns?: PurgeCdn[];
}

/** One Cloudflare cache-tag purge outcome (shared by every tag in its batch). */
export interface PurgeTagResult {
  tag: string;
  ok: boolean;
  status: number;
  purgeId: string | null;
}

/** Outcome of `POST /_tr/warm` for the purged tags (the KV warm store). */
export interface PurgeWarmResult {
  ok: boolean;
  status: number;
  message: string | null;
  tags: string[];
}

/** One step of the delayed re-purge an asset rebuild schedules. */
export interface PurgeScheduleStep {
  afterSeconds: number;
  /** ISO time the step is due to run. */
  at: string;
}

export interface PurgeResponse {
  mechanism: PurgeMechanismKind;
  /** Per-URL outcomes: Fastly soft purge, or Cloudflare URLs for the Hebrew site. */
  results: PurgeUrlResult[];
  /**
   * An upstream rebuild call. Always null since 2026-11-04: the legacy assets
   * rebuild (`pushToQ` on tr-fe-assets-func) was removed with its host.
   */
  upstream: {
    url: string;
    status: "completed" | "started" | "failed";
    message: string | null;
  } | null;
  /** Pages skipped because a required param was missing or out of range. */
  skipped: { pageKey: string; reason: string }[];
  /** Every CDN this request reached (immediately or on `schedule`). */
  cdns: PurgeCdn[];
  /** Cloudflare tag purge outcomes made while handling the request. */
  tags: PurgeTagResult[];
  /** `/_tr/warm` outcome for those tags; null when no tag purge ran now. */
  warm: PurgeWarmResult | null;
  /**
   * Delayed Cloudflare tag purges scheduled after a legacy asset rebuild. The
   * rebuild queue gives no completion signal, so the purge repeats; empty when
   * nothing is scheduled. Timers run in the API process — re-run after a restart.
   */
  schedule: PurgeScheduleStep[];
}
